import React, { useContext, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Grid, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { Add, DeleteOutline, Edit, SelfImprovement, Schedule, MusicNote } from '@mui/icons-material';
import AppBarContext from 'context/AppBarContext';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import PlayRecoveryPracticeDrawer from 'components/play/admin/PlayRecoveryPracticeDrawer';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import {
  ADMIN_PLAY_RECOVERY_PRACTICES,
  PLAY_RECOVERY_PRACTICES,
  DELETE_PLAY_RECOVERY_PRACTICE
} from 'api/graphql/playRecovery';

/**
 * OTRIX Play (admin/entrenadora) — gestor de prácticas de recuperación: las 4 del sistema
 * (editables, no borrables) + las custom de la entrenadora con secuencias dinámicas.
 */
function PlayRecoveryAdminPage() {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const pageTitle = t('recoveryAdmin.pageTitle', { ns: 'play', defaultValue: 'Prácticas de descanso' });

  const [editorPractice, setEditorPractice] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const { data, loading } = useQuery(ADMIN_PLAY_RECOVERY_PRACTICES, { fetchPolicy: 'cache-and-network' });
  const [deletePractice] = useMutation(DELETE_PLAY_RECOVERY_PRACTICE, {
    refetchQueries: [{ query: ADMIN_PLAY_RECOVERY_PRACTICES }, { query: PLAY_RECOVERY_PRACTICES }]
  });

  const practices = data?.adminPlayRecoveryPractices || [];
  const firstLoad = loading && !data;

  const titleOf = (p) => (p.key ? t(`recovery.blocks.${p.key}.title`, { ns: 'play' }) : p.title || '');

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deletePractice({ variables: { id: target.id } });
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({ key: `rec-del-${Date.now()}`, variant: 'success', message: t('recoveryAdmin.deleted', { ns: 'play', defaultValue: 'Práctica eliminada.' }) })
        )
      );
    } catch {
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({ key: `rec-del-err-${Date.now()}`, variant: 'error', message: t('recoveryAdmin.deleteError', { ns: 'play', defaultValue: 'No pudimos eliminar la práctica.' }) })
        )
      );
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <SEOPageTitle title={pageTitle} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack spacing={0.25}>
          <Typography variant="h5" fontWeight={800} letterSpacing="-0.01em">
            {pageTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('recoveryAdmin.subtitle', { ns: 'play', defaultValue: 'Lo que los clientes practican en sus días de descanso: respiración, yoga, movilidad y tus propias secuencias.' })}
          </Typography>
        </Stack>
        <Button
          startIcon={<Add />}
          onClick={() => {
            setEditorPractice(null);
            setEditorOpen(true);
          }}
          sx={{ ...premiumButtonSx, flex: 'none' }}>
          {t('recoveryAdmin.new', { ns: 'play', defaultValue: 'Nueva práctica' })}
        </Button>
      </Stack>

      {firstLoad && (
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: '16px' }} />
            </Grid>
          ))}
        </Grid>
      )}

      {!firstLoad && practices.length === 0 && (
        <PremiumCard>
          <Stack spacing={1.5} alignItems="center" sx={{ py: 4, textAlign: 'center' }}>
            <SelfImprovement sx={{ fontSize: 44, color: 'text.disabled' }} />
            <Typography variant="h6" fontWeight={800}>
              {t('recoveryAdmin.emptyTitle', { ns: 'play', defaultValue: 'Aún no hay prácticas' })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 380 }}>
              {t('recoveryAdmin.emptyBody', { ns: 'play', defaultValue: 'Cuando corra la migración verás aquí las prácticas del sistema, y podrás crear las tuyas.' })}
            </Typography>
          </Stack>
        </PremiumCard>
      )}

      {!firstLoad && practices.length > 0 && (
        <Grid container spacing={2}>
          {practices.map((practice) => {
            const isSystem = Boolean(practice.key);
            const stepCount = (practice.steps || []).filter((s) => s.type !== 'rest').length;
            return (
              <Grid item xs={12} sm={6} key={practice.id}>
                <Stack
                  spacing={1.25}
                  sx={{
                    height: '100%',
                    p: 2,
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'hairline.main',
                    backgroundColor: 'surface.content',
                    boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight)',
                    transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1)',
                    '&:hover': { transform: 'translateY(-2px)' }
                  }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SelfImprovement sx={{ color: 'gold.main', fontSize: 20, flex: 'none' }} />
                    <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {titleOf(practice)}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={t('recoveryAdmin.edit', { ns: 'play', defaultValue: 'Editar práctica' })}
                      onClick={() => {
                        setEditorPractice(practice);
                        setEditorOpen(true);
                      }}
                      sx={{ color: 'text.secondary' }}>
                      <Edit sx={{ fontSize: 17 }} />
                    </IconButton>
                    {!isSystem && (
                      <IconButton
                        size="small"
                        aria-label={t('recoveryAdmin.delete', { ns: 'play', defaultValue: 'Eliminar práctica' })}
                        onClick={() => setDeleteTarget(practice)}
                        sx={{ color: 'sessionStatus.absent.fg' }}>
                        <DeleteOutline sx={{ fontSize: 17 }} />
                      </IconButton>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={t(`recoveryAdmin.categories.${practice.category}`, { ns: 'play', defaultValue: practice.category })}
                      sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700, height: 20, fontSize: '0.66rem' }}
                    />
                    {isSystem && (
                      <Chip
                        size="small"
                        label={t('recoveryAdmin.system', { ns: 'play', defaultValue: 'Sistema' })}
                        sx={{ bgcolor: 'surface.overlay', color: 'text.secondary', fontWeight: 700, height: 20, fontSize: '0.66rem' }}
                      />
                    )}
                    {!practice.is_active && (
                      <Chip
                        size="small"
                        label={t('musicAdmin.inactive', { ns: 'play', defaultValue: 'Oculta' })}
                        sx={{ bgcolor: 'surface.overlay', color: 'text.secondary', fontWeight: 700, height: 20, fontSize: '0.66rem' }}
                      />
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ color: 'text.secondary', mt: 'auto' }}>
                    <Stack direction="row" spacing={0.4} alignItems="center">
                      <Schedule sx={{ fontSize: 13 }} />
                      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {t('todayCard.minutes', { ns: 'play', count: practice.minutes })}
                      </Typography>
                    </Stack>
                    {stepCount > 0 && (
                      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {t('recoveryAdmin.stepCount', { ns: 'play', count: stepCount, defaultValue: '{{count}} pasos' })}
                      </Typography>
                    )}
                    {practice.music_track && (
                      <Stack direction="row" spacing={0.4} alignItems="center">
                        <MusicNote sx={{ fontSize: 13, color: 'gold.main' }} />
                        <Typography variant="caption" noWrap>
                          {practice.music_track.title}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      )}

      <PlayRecoveryPracticeDrawer open={editorOpen} practice={editorPractice} onClose={() => setEditorOpen(false)} />

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('recoveryAdmin.deleteTitle', { ns: 'play', defaultValue: 'Eliminar práctica' })}
        message={t('recoveryAdmin.deleteMessage', {
          ns: 'play',
          name: deleteTarget ? deleteTarget.title || '' : '',
          defaultValue: '¿Eliminar "{{name}}"? Los clientes dejarán de verla; su historial se conserva.'
        })}
      />
    </Box>
  );
}

export default PlayRecoveryAdminPage;
